import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button, Field, Input, Select, Modal } from "@/components/ui-kit";
import { parseIDR, formatIDR } from "@/lib/format";

const PRICE_FIELDS = [
  { key: "price_non_vat", label: "Harga Non PPN" },
  { key: "price_ex_vat", label: "Harga Exc PPN" },
  { key: "price_with_vat", label: "Harga + PPN" },
  { key: "delivery_cost", label: "Biaya Kirim" },
  { key: "unloading_fee", label: "Biaya Bongkar" },
];

/**
 * Bulk Price Edit Modal
 * - Direction: turun | naik | tetap
 * - Mode: nominal | persen (irrelevant when direction=tetap)
 * - Delta value
 * - Targeted price fields (checkbox multi-select; at least one required)
 * - Applies to the array of selected price rows; PATCHes each via /api/prices/{id}
 * - "Harga Exc PPN" is server-recomputed from price_with_vat / 1.11; user can still tick it but
 *   on rows where Harga + PPN is being changed, the derived value will overwrite anyway.
 */
export function BulkPriceEditModal({ open, onClose, selectedRows, onDone }) {
  const [direction, setDirection] = useState("");
  const [mode, setMode] = useState("");
  const [delta, setDelta] = useState("");
  const [fields, setFields] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setDirection(""); setMode(""); setDelta(""); setFields({});
  };
  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const toggleField = (k) => setFields((f) => ({ ...f, [k]: !f[k] }));
  const chosenFields = Object.keys(fields).filter((k) => fields[k]);

  const computeNew = (oldVal) => {
    const o = parseIDR(oldVal) || 0;
    const d = parseFloat(String(delta || "0").replace(",", ".")) || 0;
    if (direction === "tetap") return o;
    if (direction === "turun" && mode === "nominal") return Math.max(0, o - d);
    if (direction === "turun" && mode === "persen") return Math.max(0, o - (o * d) / 100);
    if (direction === "naik" && mode === "nominal") return o + d;
    if (direction === "naik" && mode === "persen") return o + (o * d) / 100;
    return o;
  };

  const apply = async () => {
    if (!direction) { toast.error("Pilih arah perubahan"); return; }
    if (direction !== "tetap" && (!mode || delta === "")) { toast.error("Lengkapi mode dan nilai"); return; }
    if (chosenFields.length === 0) { toast.error("Pilih minimal satu kolom harga"); return; }
    if (selectedRows.length === 0) { toast.error("Tidak ada baris dipilih"); return; }
    setSubmitting(true);
    let success = 0;
    let failed = 0;
    for (const row of selectedRows) {
      const updates = {};
      for (const fk of chosenFields) {
        const newVal = Math.round(computeNew(row[fk]) * 100) / 100;
        updates[fk] = newVal;
      }
      // Always recompute Harga Exc PPN from Harga + PPN if either is touched
      const withVatTarget = "price_with_vat" in updates ? updates.price_with_vat : parseIDR(row.price_with_vat);
      if (withVatTarget > 0) updates.price_ex_vat = Math.round((withVatTarget / 1.11) * 100) / 100;
      try {
        await api.patch(`/prices/${row.id}`, {
          ...row,  // server may expect full body for some setups
          ...updates,
          client_name: "",  // ensure cleaned
        });
        success++;
      } catch {
        failed++;
      }
    }
    setSubmitting(false);
    if (failed === 0) toast.success(`${success} harga diperbarui ✓`);
    else toast.error(`${success} berhasil, ${failed} gagal`);
    reset();
    onDone?.();
    onClose?.();
  };

  const previewRow = selectedRows[0];

  return (
    <Modal open={open} onClose={handleClose} title={`Edit Massal Harga (${selectedRows.length} baris)`} size="lg">
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
          <b>{selectedRows.length}</b> baris harga akan diperbarui sekaligus. Aksi ini tidak bisa di-undo otomatis — pastikan settingan sudah benar sebelum klik Terapkan.
        </div>
        <div className="grid grid-cols-12 gap-3 items-end">
          <div className="col-span-12 md:col-span-4">
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Arah Perubahan</label>
            <Select value={direction} onChange={(e) => { setDirection(e.target.value); if (e.target.value === "tetap") { setMode(""); setDelta(""); } }} data-testid="bulk-edit-direction">
              <option value="">— Pilih —</option>
              <option value="turun">Harga Turun</option>
              <option value="naik">Harga Naik</option>
              <option value="tetap">Harga Tetap</option>
            </Select>
          </div>
          <div className="col-span-6 md:col-span-3">
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Mode</label>
            <Select value={mode} onChange={(e) => setMode(e.target.value)} disabled={!direction || direction === "tetap"} data-testid="bulk-edit-mode">
              <option value="">— Pilih —</option>
              <option value="nominal">Nominal</option>
              <option value="persen">Persentase</option>
            </Select>
          </div>
          <div className="col-span-6 md:col-span-5">
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">{mode === "persen" ? "Nilai (%)" : "Nominal (Rp)"}</label>
            <Input className="font-mono-num" value={delta} onChange={(e) => setDelta(e.target.value)} disabled={!mode} placeholder={mode === "persen" ? "0" : "0"} data-testid="bulk-edit-delta" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-2">Kolom harga yang akan di-update (centang minimal 1)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 border border-stone-200 p-3 bg-stone-50/50">
            {PRICE_FIELDS.map((f) => (
              <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!fields[f.key]} onChange={() => toggleField(f.key)} data-testid={`bulk-edit-field-${f.key}`} />
                <span>{f.label}</span>
              </label>
            ))}
          </div>
          <p className="text-[10px] text-stone-500 mt-1">* Saat <b>Harga + PPN</b> diperbarui, <b>Harga Exc PPN</b> otomatis disesuaikan ke nilai_baru ÷ 1,11.</p>
        </div>
        {previewRow && direction && (
          <div className="border border-stone-200 p-3 text-xs">
            <p className="font-semibold text-stone-700 mb-1">Preview pada baris pertama (<i>{previewRow.material_name}</i>):</p>
            <ul className="space-y-0.5 font-mono">
              {chosenFields.map((fk) => {
                const label = PRICE_FIELDS.find((p) => p.key === fk)?.label || fk;
                const oldV = parseIDR(previewRow[fk]) || 0;
                const newV = computeNew(previewRow[fk]);
                const diff = newV - oldV;
                return (
                  <li key={fk} className={diff > 0 ? "text-emerald-700" : diff < 0 ? "text-red-700" : "text-stone-500"}>
                    {label}: {formatIDR(oldV)} → <b>{formatIDR(newV)}</b> {diff !== 0 && `(${diff > 0 ? "+" : ""}${formatIDR(diff)})`}
                  </li>
                );
              })}
              {chosenFields.length === 0 && <li className="text-stone-400 italic">Belum ada kolom dipilih.</li>}
            </ul>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>Batal</Button>
          <Button onClick={apply} disabled={submitting} data-testid="bulk-edit-apply">
            {submitting ? "Memperbarui…" : `Terapkan ke ${selectedRows.length} baris`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
